import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAdminContext } from '../context/AdminContext';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { LogIn, ShieldAlert, KeyRound, User } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { login } = useAdminContext();
    const navigate = useNavigate();
    const location = useLocation();

    // Redirect to the page they were trying to access or to admin dashboard
    const from = location.state?.from?.pathname || "/admin";

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        // Artificial delay for better UX
        setTimeout(() => {
            const success = login(username, password);
            if (success) {
                toast.success('Login Successful', {
                    description: 'Welcome back to the Admin Panel.'
                });
                navigate(from, { replace: true });
            } else {
                toast.error('Login Failed', {
                    description: 'Invalid username or password.'
                });
            }
            setIsLoading(false);
        }, 800);
    };

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-red-600 text-white rounded-2xl shadow-xl mb-4">
                        <KeyRound className="w-8 h-8" />
                    </div>
                    <h1 className="text-3xl font-bold text-slate-900">MediSeller Admin</h1>
                    <p className="text-slate-500 mt-2">Secure access for pharmacy management</p>
                </div>

                <Card className="border-none shadow-2xl">
                    <CardHeader className="space-y-1">
                        <CardTitle className="text-2xl">Login</CardTitle>
                        <CardDescription>
                            Enter your administrative credentials to continue
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="grid gap-4">
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid gap-2">
                                <Label htmlFor="username">Username</Label>
                                <div className="relative">
                                    <User className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                                    <Input
                                        id="username"
                                        type="text"
                                        placeholder="Enter your username"
                                        className="pl-10"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="grid gap-2">
                                <Label htmlFor="password">Password</Label>
                                <div className="relative">
                                    <ShieldAlert className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                                    <Input
                                        id="password"
                                        type="password"
                                        placeholder="••••••••"
                                        className="pl-10"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>
                            <Button
                                type="submit"
                                className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg font-bold transition-all"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <span className="flex items-center gap-2">
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                        Verifying...
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-2">
                                        <LogIn className="w-5 h-5" />
                                        Log In
                                    </span>
                                )}
                            </Button>
                        </form>
                    </CardContent>
                    <CardFooter className="flex flex-col border-t bg-slate-50/50 p-6 rounded-b-xl">
                        <p className="text-xs text-center text-slate-400">
                            Authorized personnel only. All access attempts are monitored and recorded.
                        </p>
                    </CardFooter>
                </Card>

                <p className="text-center mt-8 text-sm text-slate-400">
                    &copy; 2025 MediSeller Pharmacy Hub. All rights reserved.
                </p>
            </div>
        </div>
    );
};

export default LoginPage;
